<script setup lang="ts">
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import { PlanBenefitCostType, PolicyType } from '~/generated/api-clients-generated'

  const props = defineProps<{
    quote: QuotedPlanVm
    id: string
    stacked?: boolean
  }>()

  const { getId } = Id.use(props.id)
  const { show } = DialogController.use()

  const monthlyDrugCostAverage = computed(() => {
    const monthlyCosts = props.quote?.drugCoverage?.pharmacyCosts?.[0]?.monthlyCosts ?? []
    if (monthlyCosts.length === 0) return 0
    const total = _sumBy(monthlyCosts, (month) => _sumBy(month.details, (d) => d.memberCost ?? 0))
    return total / monthlyCosts.length
  })

  const formatCopay = (copay: any) => {
    if (!copay) return '—'
    return copay.amountType === PlanBenefitCostType.Coinsurance
      ? formatPct(copay.amount)
      : formatUsd(copay.amount)
  }

  const secondaryStats = computed(() => [
    {
      label: 'PCP Copay',
      value: formatCopay(props.quote?.details?.providerBenefits?.doctorCopay)
    },
    {
      label: 'Specialist Copay',
      value: formatCopay(props.quote?.details?.providerBenefits?.specialistCopay)
    },
    {
      label: 'Max Out-of-Pocket',
      value: formatUsd(props.quote?.details?.maxOutOfPocket ?? 0)
    }
  ])

  const isPpo = computed(() => [PolicyType.PPO, PolicyType.LPPO].includes(props.quote.policyType))
  const hasDoctors = computed(() => props.quote.providerCoverage.count > 0)
  const hasDrugs = computed(() => props.quote.drugCoverage.count > 0)

  const inNetworkDoctorClasses = computed(() => {
    if (props.quote.providerCoverage.inNetworkCount === props.quote.providerCoverage.count)
      return 'text-success'
    if (props.quote.providerCoverage.inNetworkCount === 0) return 'text-danger'
    return 'text-primary-900'
  })
</script>

<template>
  <ul
    class="primary-stats mx-auto grid w-full justify-center gap-3 text-base"
    :class="stacked ? 'grid-cols-4' : 'grid-cols-4 md:grid-cols-3 md:px-7 lg:px-14'"
  >
    <li
      class="flex flex-1 flex-col items-center justify-center whitespace-nowrap rounded-xl border border-border-light bg-gray-200/40 px-3 pt-3 text-center leading-tight text-gray-800"
      :class="stacked ? 'col-span-4' : 'col-span-4 md:col-span-1'"
    >
      Monthly Premium
      <span
        class="flex min-h-[75px] items-center pb-2 text-4xl font-bold leading-none !tracking-tighter sm:text-5xl lg:text-[2.75rem]"
        :class="(quote.recommendation.premium ?? 0) === 0 ? 'text-success' : 'text-primary-900'"
      >
        {{ formatUsd(quote.recommendation.premium ?? 0) }}
      </span>
    </li>

    <li
      class="relative rounded-xl border bg-gray-200/50 px-3 pt-3"
      :class="[
        stacked ? 'col-span-4 sm:col-span-2' : 'col-span-4 xs:col-span-2 md:col-span-1',
        hasDoctors ? 'border-border-light' : 'border-dashed border-border'
      ]"
    >
      <HpToolTip
        v-if="isPpo && hasDoctors"
        log
        title="PPO Flexibility"
        body="PPO networks allow you to see out-of-network doctors and specialists without referrals."
        class="ppo-badge"
      >
      </HpToolTip>

      <HpAnchor
        v-if="hasDoctors"
        @click.prevent="show(getId('doctors-sidebar'))"
        class="flex h-full flex-1 flex-col items-center justify-center whitespace-nowrap text-center leading-tight no-underline"
      >
        <span
          class="flex items-center gap-1 whitespace-nowrap text-base leading-tight text-gray-800"
        >
          Doctors In-Network
          <HpIcon name="lucide:info" class="size-3.5 text-gray-700" />
        </span>
        <span
          class="flex min-h-[75px] items-center pb-2 text-4xl font-bold leading-none !tracking-wider sm:text-5xl lg:text-[2.75rem]"
          :class="inNetworkDoctorClasses"
        >
          {{ quote.providerCoverage.inNetworkCount }}/{{ quote.providerCoverage.count }}
        </span>
      </HpAnchor>
      <HpAnchor
        v-else
        @click.prevent="show('update-insurance-sidebar')"
        class="grid h-full place-items-center gap-1 text-balance pb-2 text-center font-semibold leading-tight no-underline"
      >
        <span
          class="flex items-center gap-1 whitespace-nowrap text-base leading-tight tracking-tight text-gray-800"
        >
          Doctors In-Network
          <HpIcon name="lucide:info" class="size-3.5 text-gray-700" />
        </span>
        <HpIcon
          name="help-simple"
          class="size-10 rounded-full border-[3px] border-dashed border-border bg-white p-0.5 text-gray-500"
        />
        <span class="-mt-1">Add Your Doctors</span>
      </HpAnchor>
    </li>

    <li
      class="rounded-xl border bg-gray-200/50 px-3 pt-3"
      :class="[
        stacked ? 'col-span-4 sm:col-span-2' : 'col-span-4 xs:col-span-2 md:col-span-1',
        hasDrugs ? 'border-border-light' : 'border-dashed border-border'
      ]"
    >
      <HpAnchor
        v-if="hasDrugs"
        @click.prevent="show(getId('drug-monthly-card-breakdown-sidebar'))"
        class="flex h-full flex-1 flex-col items-center justify-center whitespace-nowrap text-center leading-tight no-underline"
      >
        <span
          class="flex items-center gap-1 whitespace-nowrap text-base leading-tight text-gray-800"
        >
          Est Drug Cost/Mo
          <HpIcon name="lucide:info" class="size-3.5 text-gray-700" />
        </span>
        <span
          class="flex min-h-[75px] items-center pb-2 text-4xl font-bold leading-none !tracking-tighter sm:text-5xl lg:text-[2.75rem]"
          :class="monthlyDrugCostAverage === 0 ? 'text-success' : 'text-primary-900'"
        >
          {{ formatUsd(monthlyDrugCostAverage) }}
        </span>
      </HpAnchor>
      <HpAnchor
        v-else
        @click.prevent="show('update-insurance-sidebar')"
        class="grid h-full place-items-center gap-1 text-balance pb-2 text-center font-semibold leading-tight no-underline"
      >
        <span
          class="flex items-center gap-1 whitespace-nowrap text-base leading-tight tracking-tight text-gray-800"
        >
          Est Drug Cost/Mo
          <HpIcon name="lucide:info" class="size-3.5 text-gray-700" />
        </span>
        <HpIcon
          name="help-simple"
          class="size-10 rounded-full border-[3px] border-dashed border-border bg-white p-0.5 text-gray-500"
        />
        <span class="-mt-1">Add Your Drugs</span>
      </HpAnchor>
    </li>

    <li class="col-span-full flex flex-1 items-center justify-center">
      <ul class="flex w-full flex-wrap gap-3">
        <li
          v-for="stat in secondaryStats"
          :key="stat.label"
          class="flex flex-1 flex-col items-center justify-center gap-x-2 whitespace-nowrap rounded-xl border border-border-light bg-gray-200/40 px-5 pt-1.5 text-center leading-none text-gray-800 lg:flex-row lg:py-0.5"
        >
          {{ stat.label }}
          <span class="text-lg font-bold text-primary-900">{{ stat.value }}</span>
        </li>
      </ul>
    </li>
  </ul>

  <QuoteDoctorNetworkSidebar :id="getId('doctors-sidebar')" :quote="quote" />
  <DetailsDrugMonthlyBreakdownSidebarV2
    :id="getId('drug-monthly-card-breakdown-sidebar')"
    :quote="quote"
  />
</template>

<style scoped>
  .ppo-badge {
    @apply absolute -top-2.5 left-1/2 flex !h-5 -translate-x-1/2 items-center rounded-full border border-success/20 bg-success-light px-2 text-sm font-semibold text-success-dark;

    & :deep(.target-text) {
      @apply whitespace-nowrap !border-none;
    }
  }
</style>
